import {
    ActionIcon,
    Alert,
    Button,
    Grid,
    Group,
    Loader,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {IconBriefcase, IconTrash} from '@tabler/icons-react'
import {useEffect, useMemo, useState} from 'react'

import {GroupedEquipmentAssignmentCard} from 'src/equipment/grouped-equipment-assignment-card'
import {stockSourceKey} from 'src/equipment/stock-source'

import type {RentalAsset} from '../assets/rental-assets/schemas'
import type {ThirdPartyRentalAssetOffering} from '../assets/rental-assets/schemas'
import {useRentalAssetTypes} from '../assets/rental-assets/use-rental-asset-types'
import {useRentalAssets} from '../assets/rental-assets/use-rental-assets'
import {useThirdPartyRentalAssetOfferings} from '../assets/rental-assets/use-third-party-rental-asset-offerings'
import {useWorkerTypes} from '../assets/worker-types/use-worker-types'
import type {Worker} from '../assets/workers/schemas'
import {useWorkers} from '../assets/workers/use-workers'
import {useInvalidateDispatchAttentionCount} from '../dispatch/use-dispatch'
import {useInvalidateProjectDeployedEquipment} from '../project-deployed-equipment/use-project-deployed-equipment'
import {
    addAssignment,
    assignmentsToInputs,
    fulfillmentAssignedQuantity,
    type FulfillmentGroup,
    getAvailablePool,
    isFirstPartyFleetRentalAssetId,
    maxBulkAssignQuantityForGroup,
    removeAssignment,
    requirementsToInputs,
    thirdPartyOfferingLabel,
} from './assignment-grouping-utils'
import {truckAndDriverBillingWarning} from './billing-checks'
import {
    billingTypeForRate,
    CREW_BILLING_TYPE_OPTIONS,
    EQUIPMENT_BILLING_TYPE_OPTIONS,
    type RequirementBillingType,
} from './billing-types'
import {FulfillmentStatusBadge} from './fulfillment-status-badge'
import type {JobDataLayer} from './job-data-layer'
import {parseApiDate} from './job-schedule-format'
import {
    billsEachLine,
    lineTakesRate,
    orderRatePhrase,
    RENTAL_BILLING_TYPE_OPTIONS,
    requirementsAsBilled,
} from './order-billing'
import {ResourceAssignmentCard} from './resource-assignment-card'
import {
    useApplyStockEquipmentAssignmentDelta,
    useAssignableStockEquipment,
} from './stock-equipment/use-stock-equipment'
import {TruckDriverBillingAlert} from './truck-driver-billing-warning'
import type {JobDetail} from './types'
import {useUnitsOnRent} from './units-on-rent/use-units-on-rent'

type ManagePoolModalProps = {
    opened: boolean
    group: FulfillmentGroup | null
    job: JobDetail
    workers: Worker[]
    rentalAssets: RentalAsset[]
    isStockType?: boolean
    jobDataLayer: JobDataLayer
    onClose: () => void
    onJobUpdated: (job: JobDetail) => void
}

function modalTitle(group: FulfillmentGroup): string {
    if (group.kind === 'worker') {
        return 'Assign workers'
    }
    return `Assign ${group.label}`
}

function resourceNameFromAssignment(
    assignment: FulfillmentGroup['assigned'][number],
    workersById: Map<string, Worker>,
    rentalAssetsById: Map<string, RentalAsset>,
): string {
    if (assignment.workerId != null) {
        return (
            assignment.workerName ?? workersById.get(assignment.workerId)?.name ?? 'Unnamed worker'
        )
    }

    if (assignment.rentalAssetId != null) {
        const asset = rentalAssetsById.get(assignment.rentalAssetId)
        return (
            assignment.rentalAssetName ??
            asset?.name ??
            asset?.rentalAssetTypeName ??
            'Unnamed asset'
        )
    }

    return 'Unnamed'
}

export function ManagePoolModal({
    opened,
    group,
    job,
    workers: parentWorkers,
    rentalAssets: parentRentalAssets,
    isStockType = false,
    jobDataLayer,
    onClose,
    onJobUpdated,
}: ManagePoolModalProps) {
    const [workers, setWorkers] = useState<Worker[]>(parentWorkers)
    const [rentalAssets, setRentalAssets] = useState<RentalAsset[]>(parentRentalAssets)
    const [offerings, setOfferings] = useState<ThirdPartyRentalAssetOffering[]>([])
    const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null)
    const [offeringQuantity, setOfferingQuantity] = useState(1)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [bulkAssignQuantity, setBulkAssignQuantity] = useState(1)
    const [billingRate, setBillingRate] = useState('')
    const [billingType, setBillingType] = useState<RequirementBillingType | null>(null)
    const [billingWarning, setBillingWarning] = useState<string | null>(null)
    const editsRequirement = opened && group?.requirementId != null
    const {data: workerTypes = []} = useWorkerTypes({enabled: editsRequirement})
    const {data: rentalAssetTypes = []} = useRentalAssetTypes({enabled: editsRequirement})

    const invalidateDispatchAttentionCount = useInvalidateDispatchAttentionCount()
    const invalidateProjectDeployedEquipment = useInvalidateProjectDeployedEquipment()
    const [vendorQuantity, setVendorQuantity] = useState<number | string>(1)
    const [removeQuantities, setRemoveQuantities] = useState<Record<string, number | string>>({})
    const applyStockDelta = useApplyStockEquipmentAssignmentDelta(job.id)
    const {data: assignableStock, isLoading: assignableStockLoading} = useAssignableStockEquipment(
        job.id,
        group?.rentalAssetTypeId,
        {enabled: opened && isStockType && group?.rentalAssetTypeId != null},
    )
    const jobStartDate = useMemo(() => parseApiDate(job.startDate), [job.startDate])
    const needsWorkerPool = opened && group?.kind === 'worker' && parentWorkers.length === 0
    const needsRentalPool = opened && group?.kind === 'rental_asset'

    const {data: fetchedWorkers, isLoading: workersLoading} = useWorkers({
        enabled: needsWorkerPool,
    })
    const {data: modalWorkers, isLoading: modalWorkersLoading} = useWorkers({
        enabled: needsRentalPool,
    })
    const {data: modalRentalAssets, isLoading: rentalAssetsLoading} = useRentalAssets({
        rentalAssetTypeId: group?.rentalAssetTypeId ?? undefined,
        enabled: needsRentalPool && !isStockType,
    })
    const {data: unitsOnRent, isLoading: unitsOnRentLoading} = useUnitsOnRent(
        job.id,
        group?.rentalAssetTypeId,
        {enabled: needsRentalPool && !isStockType},
    )
    const {data: modalOfferings, isLoading: offeringsLoading} = useThirdPartyRentalAssetOfferings(
        {rentalAssetTypeId: group?.rentalAssetTypeId ?? undefined},
        {enabled: needsRentalPool},
    )

    const loading =
        saving ||
        (needsWorkerPool && workersLoading) ||
        (needsRentalPool &&
            !isStockType &&
            (modalWorkersLoading ||
                rentalAssetsLoading ||
                offeringsLoading ||
                unitsOnRentLoading)) ||
        (needsRentalPool && isStockType && assignableStockLoading)

    useEffect(() => {
        setWorkers(parentWorkers)
    }, [parentWorkers])

    useEffect(() => {
        setRentalAssets(parentRentalAssets)
    }, [parentRentalAssets])

    useEffect(() => {
        if (!group) {
            return
        }
        setQuantity(group.needed > 0 ? group.needed : 1)

        // Set billing fields from the requirement
        const requirement = job.requirements.find((req) => req.id === group.requirementId)
        if (requirement) {
            setBillingRate(requirement.billingRate ?? '')
            setBillingType(requirement.billingType)
        } else {
            setBillingRate('')
            setBillingType(null)
        }
    }, [group, job.requirements])

    useEffect(() => {
        if (!opened || !group) {
            return
        }
        setSelectedOfferingId(null)
        setErrorMessage(null)
        setBulkAssignQuantity(1)

        if (group.kind === 'worker') {
            if (parentWorkers.length > 0) {
                return
            }
            setWorkers(fetchedWorkers ?? [])
            setRentalAssets([])
            setOfferings([])
            return
        }

        setWorkers(modalWorkers ?? [])
        if (!isStockType) {
            setRentalAssets(modalRentalAssets ?? [])
        }
        setOfferings(modalOfferings ?? [])
    }, [
        opened,
        group,
        parentWorkers,
        fetchedWorkers,
        modalWorkers,
        modalRentalAssets,
        modalOfferings,
        isStockType,
    ])

    const firstPartyRentalAssetIds = useMemo(
        () => new Set(rentalAssets.map((asset) => asset.id)),
        [rentalAssets],
    )

    if (!group) {
        return null
    }

    const currentGroup = group

    const savedRequirement = job.requirements.find((req) => req.id === group.requirementId)
    const eachLine = billsEachLine(job.billingMethod)
    const isRentalType = (typeId: string | null | undefined) =>
        typeId != null &&
        rentalAssetTypes.find((type) => type.id === typeId)?.isDrivenTruck !== true
    const groupTakesRate = lineTakesRate(job.billingMethod, {
        isRentalEquipment: group.kind !== 'worker' && isRentalType(group.rentalAssetTypeId),
    })
    const billingDirty =
        group.requirementId != null &&
        (savedRequirement?.billingType !== billingType ||
            (savedRequirement?.billingRate ?? null) !== (billingRate.trim() || null))

    const workersById = new Map(workers.map((worker) => [worker.id, worker]))
    const rentalAssetsById = new Map(rentalAssets.map((asset) => [asset.id, asset]))
    const assignmentInputs = assignmentsToInputs(job.assignments)
    const availablePool = getAvailablePool(
        group,
        job.assignments,
        workers,
        rentalAssets,
        new Map((unitsOnRent ?? []).map((unit) => [unit.rentalAssetId, unit.label])),
    )
    const assignableCount = availablePool.filter((resource) => !resource.unavailableReason).length
    const maxBulkQuantity = isStockType
        ? maxBulkAssignQuantityForGroup(group, assignableStock?.assignableQuantity ?? 0)
        : 0
    const assignmentDisplay = group.assigned.map((item) => ({type: 'single' as const, item}))
    const requirementGap = Math.max(0, group.needed - fulfillmentAssignedQuantity(group))
    const offeringOptions = offerings
        .map((offering) => ({
            value: offering.id,
            label: thirdPartyOfferingLabel(offering),
        }))
        .sort((left, right) => left.label.localeCompare(right.label))

    function rentalAssetPeekFor(assetId: string) {
        if (
            currentGroup.kind !== 'rental_asset' ||
            isStockType ||
            !isFirstPartyFleetRentalAssetId(assetId, firstPartyRentalAssetIds)
        ) {
            return undefined
        }

        return {
            assetId,
            assetTypeName: currentGroup.label,
            referenceDate: jobStartDate,
        }
    }

    function workerAssignmentPeekFor(workerId: string) {
        if (currentGroup.kind !== 'worker') {
            return undefined
        }

        return {
            workerId,
            workerTypeName: currentGroup.label,
            referenceDate: jobStartDate,
        }
    }

    async function persistAssignments(nextAssignments: ReturnType<typeof assignmentsToInputs>) {
        if (!group) {
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.saveAssignments(job.id, nextAssignments)
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save assignments')
        } finally {
            setSaving(false)
        }
    }

    function handleBillingRateChange(value: string) {
        setBillingRate(value)
        setErrorMessage(null)
        setBillingWarning(null)
        if (value.trim() === '' || !group) {
            return
        }

        const isTruck =
            group.kind !== 'worker' &&
            rentalAssetTypes.some(
                (type) => type.id === group.rentalAssetTypeId && type.isDrivenTruck,
            )
        setBillingType((current) =>
            billingTypeForRate(
                value,
                current,
                group.kind === 'worker' ? 'worker' : isTruck ? 'truck' : 'equipment',
            ),
        )
    }

    async function persistRequirements(
        nextQuantity: number,
        {confirmed = false}: {confirmed?: boolean} = {},
    ) {
        if (!group || group.requirementId == null) {
            return
        }

        const hasBillingRate = billingRate.trim() !== ''
        if (billingType !== null && !hasBillingRate) {
            setErrorMessage('Billing rate is required')
            return
        }
        if (hasBillingRate && isNaN(Number(billingRate))) {
            setErrorMessage('Billing rate must be a valid number')
            return
        }
        if (hasBillingRate && Number(billingRate) < 0) {
            setErrorMessage('Billing rate must not be negative')
            return
        }

        const nextRequirements = requirementsAsBilled(
            job.billingMethod,
            requirementsToInputs(job.requirements).map((requirement) =>
                requirement.id === group.requirementId
                    ? {
                          ...requirement,
                          quantity: nextQuantity,
                          billingRate: billingRate.trim() || null,
                          billingType: hasBillingRate ? billingType : null,
                      }
                    : requirement,
            ),
            (requirement) =>
                requirement.requirementType === 'rental_asset' &&
                isRentalType(requirement.rentalAssetTypeId),
        )
        const warning = billingDirty
            ? truckAndDriverBillingWarning(nextRequirements, {workerTypes, rentalAssetTypes})
            : null
        if (warning != null && !confirmed) {
            setBillingWarning(warning)
            return
        }

        setBillingWarning(null)
        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.saveRequirements(job.id, nextRequirements)
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save requirement')
        } finally {
            setSaving(false)
        }
    }

    async function handleAssign(resourceId: string) {
        if (!group) {
            return
        }
        await persistAssignments(addAssignment(assignmentInputs, group, resourceId))
    }

    async function handleAssignOffering() {
        if (!group || selectedOfferingId == null) {
            return
        }
        if (isStockType) {
            const quantity = Number(vendorQuantity)
            if (!Number.isInteger(quantity) || quantity < 1 || quantity > requirementGap) return
            await applyStockDeltaChange(quantity, selectedOfferingId)
        } else {
            await persistAssignments(
                addAssignment(
                    assignmentInputs,
                    group,
                    selectedOfferingId,
                    'third_party_offering',
                    offeringQuantity,
                ),
            )
        }
        setSelectedOfferingId(null)
        setOfferingQuantity(1)
    }

    async function handleRemove(resourceId: string) {
        await persistAssignments(removeAssignment(assignmentInputs, resourceId))
    }

    async function applyStockDeltaChange(delta: number, offeringId?: string) {
        if (!group?.rentalAssetTypeId) {
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const {job: updatedJob} = await applyStockDelta.mutateAsync({
                rentalAssetTypeId: group.rentalAssetTypeId,
                delta,
                thirdPartyRentalAssetOfferingId: offeringId,
            })
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Failed to update stock assignment',
            )
        } finally {
            setSaving(false)
        }
    }

    async function handleRemoveGroup(assignedCount: number, offeringId?: string) {
        if (!Number.isInteger(assignedCount) || assignedCount < 1) {
            return
        }
        await applyStockDeltaChange(-assignedCount, offeringId)
    }

    async function handleBulkAssign() {
        if (!group || bulkAssignQuantity < 1 || maxBulkQuantity < 1) {
            return
        }

        await applyStockDeltaChange(bulkAssignQuantity)
        setBulkAssignQuantity(1)
    }

    async function handleQuantityChange(value: number | string) {
        const nextQuantity = typeof value === 'number' ? value : 1
        setQuantity(nextQuantity)
        if (nextQuantity > 0) {
            await persistRequirements(nextQuantity)
        }
    }

    async function handleRemoveRequirement() {
        if (!group || group.requirementId == null) {
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const nextRequirements = requirementsToInputs(job.requirements).filter(
                (requirement) => requirement.id !== group.requirementId,
            )
            const updatedJob = await jobDataLayer.saveRequirements(job.id, nextRequirements)
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to remove requirement')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal opened={opened} onClose={onClose} size="xl" title={modalTitle(group)}>
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Stack gap="xs" style={{flex: 1}}>
                        <Text size="sm" c="dimmed">
                            Select resources to fulfill this requirement category.
                        </Text>
                        {group.requirementId != null && (
                            <>
                                <NumberInput
                                    label="Quantity required"
                                    min={1}
                                    value={quantity}
                                    onChange={(value) => void handleQuantityChange(value)}
                                    disabled={saving}
                                />
                                {!groupTakesRate && job.billingMethod ? (
                                    <Text size="sm" c="dimmed" data-testid="order-rate-lines-note">
                                        Billed {orderRatePhrase(job.billingMethod)}. The line takes
                                        a count only.
                                    </Text>
                                ) : null}
                                {groupTakesRate ? (
                                    <>
                                        <Select
                                            label="Billing type"
                                            data={[
                                                {
                                                    value: 'none',
                                                    label:
                                                        group.kind === 'worker'
                                                            ? "Don't bill this worker"
                                                            : "Don't bill this equipment",
                                                },
                                                ...(!eachLine
                                                    ? RENTAL_BILLING_TYPE_OPTIONS
                                                    : group.kind === 'worker'
                                                      ? CREW_BILLING_TYPE_OPTIONS
                                                      : EQUIPMENT_BILLING_TYPE_OPTIONS),
                                            ]}
                                            value={billingType ?? 'none'}
                                            onChange={(value) => {
                                                const newBillingType =
                                                    value === 'none'
                                                        ? null
                                                        : (value as RequirementBillingType)
                                                setBillingType(newBillingType)
                                                setBillingWarning(null)
                                                if (newBillingType === null) {
                                                    setBillingRate('')
                                                }
                                                setErrorMessage(null)
                                            }}
                                            disabled={saving}
                                            clearable={false}
                                        />
                                        <TextInput
                                            label="Billing rate"
                                            placeholder="0.00"
                                            value={billingRate}
                                            onChange={(event) =>
                                                handleBillingRateChange(event.currentTarget.value)
                                            }
                                            disabled={saving}
                                        />
                                        {billingWarning && (
                                            <TruckDriverBillingAlert
                                                message={billingWarning}
                                                loading={saving}
                                                onContinue={() =>
                                                    void persistRequirements(quantity, {
                                                        confirmed: true,
                                                    })
                                                }
                                                onGoBack={() => setBillingWarning(null)}
                                            />
                                        )}
                                        <Group justify="flex-end">
                                            <Button
                                                variant="light"
                                                size="xs"
                                                onClick={() => void persistRequirements(quantity)}
                                                disabled={saving || !billingDirty}
                                            >
                                                Save billing
                                            </Button>
                                        </Group>
                                    </>
                                ) : null}
                            </>
                        )}
                    </Stack>
                    <Group gap="xs" align="flex-start">
                        <Stack gap={4} align="flex-end">
                            <Text size="sm" fw={600}>
                                {fulfillmentAssignedQuantity(group)} / {group.needed} filled
                            </Text>
                            <FulfillmentStatusBadge status={group.status} />
                        </Stack>
                        {group.requirementId != null && (
                            <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label="Remove requirement"
                                onClick={() => void handleRemoveRequirement()}
                                disabled={saving}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        )}
                    </Group>
                </Group>

                {errorMessage && (
                    <Alert color="red" title="Could not save changes">
                        {errorMessage}
                    </Alert>
                )}

                <Grid>
                    <Grid.Col span={6}>
                        <Stack gap="sm">
                            <Title order={5}>Assigned</Title>
                            {isStockType
                                ? (group.stockEquipmentGroups ?? []).map((stock) => {
                                      const key = stockSourceKey(stock)
                                      return (
                                          <Stack key={key} gap="xs">
                                              <GroupedEquipmentAssignmentCard
                                                  testId={'stock-assignment-' + key}
                                                  resourceName={stock.rentalAssetTypeName}
                                                  supplierName={
                                                      stock.thirdPartyRentalCompanyName ?? undefined
                                                  }
                                                  quantity={stock.assignedQuantity}
                                                  disabled={saving}
                                                  onRemove={() =>
                                                      void handleRemoveGroup(
                                                          Math.min(
                                                              Number(
                                                                  removeQuantities[key] ??
                                                                      stock.assignedQuantity,
                                                              ),
                                                              stock.assignedQuantity,
                                                          ),
                                                          stock.thirdPartyRentalAssetOfferingId ??
                                                              undefined,
                                                      )
                                                  }
                                              />
                                              <NumberInput
                                                  label={
                                                      'Remove quantity from ' +
                                                      (stock.thirdPartyRentalCompanyName ??
                                                          'owned stock')
                                                  }
                                                  value={
                                                      removeQuantities[key] ??
                                                      stock.assignedQuantity
                                                  }
                                                  min={1}
                                                  max={stock.assignedQuantity}
                                                  allowDecimal={false}
                                                  disabled={saving}
                                                  onChange={(value) =>
                                                      setRemoveQuantities((current) => ({
                                                          ...current,
                                                          [key]: value,
                                                      }))
                                                  }
                                              />
                                          </Stack>
                                      )
                                  })
                                : null}
                            {fulfillmentAssignedQuantity(group) === 0 ? (
                                <Stack align="center" gap="xs" py="xl">
                                    <IconBriefcase size={28} color="var(--mantine-color-gray-5)" />
                                    <Text size="sm" c="dimmed">
                                        No resources assigned yet.
                                    </Text>
                                </Stack>
                            ) : (
                                !isStockType &&
                                assignmentDisplay.map((entry, index) => {
                                    const assignment = entry.item
                                    const resourceId =
                                        assignment.workerId ?? assignment.rentalAssetId
                                    if (!resourceId) {
                                        return null
                                    }

                                    return (
                                        <ResourceAssignmentCard
                                            key={`${resourceId}-${index}`}
                                            name={resourceNameFromAssignment(
                                                assignment,
                                                workersById,
                                                rentalAssetsById,
                                            )}
                                            kind={group.kind}
                                            variant="assigned"
                                            disabled={saving}
                                            rentalAssetPeek={
                                                assignment.rentalAssetId != null
                                                    ? rentalAssetPeekFor(assignment.rentalAssetId)
                                                    : undefined
                                            }
                                            workerAssignmentPeek={
                                                assignment.workerId != null
                                                    ? workerAssignmentPeekFor(assignment.workerId)
                                                    : undefined
                                            }
                                            onRemove={() => void handleRemove(resourceId)}
                                        />
                                    )
                                })
                            )}
                        </Stack>
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <Stack gap="sm">
                            <Group justify="space-between">
                                <Title order={5}>Available</Title>
                                {!loading && !isStockType && (
                                    <Text size="xs" c="dimmed">
                                        {assignableCount} matches
                                    </Text>
                                )}
                                {!loading && isStockType && assignableStock && (
                                    <Text size="xs" c="dimmed">
                                        {assignableStock.assignableQuantity} assignable
                                    </Text>
                                )}
                            </Group>

                            {loading ? (
                                <Group justify="center" py="xl">
                                    <Loader size="sm" />
                                </Group>
                            ) : isStockType ? (
                                maxBulkQuantity > 0 ? (
                                    <Stack gap="sm" data-testid="stock-bulk-assign">
                                        <Text size="sm" fw={500}>
                                            {group.label}
                                        </Text>
                                        <Group align="flex-end" wrap="nowrap">
                                            <NumberInput
                                                label="Quantity"
                                                value={bulkAssignQuantity}
                                                min={1}
                                                max={maxBulkQuantity}
                                                disabled={saving}
                                                onChange={(value) => {
                                                    const next =
                                                        typeof value === 'number'
                                                            ? value
                                                            : Number(value)
                                                    setBulkAssignQuantity(
                                                        Number.isFinite(next) ? next : 1,
                                                    )
                                                }}
                                                style={{flex: 1}}
                                            />
                                            <Button
                                                disabled={saving || bulkAssignQuantity < 1}
                                                onClick={() => void handleBulkAssign()}
                                            >
                                                Assign
                                            </Button>
                                        </Group>
                                        <Text size="xs" c="dimmed">
                                            Up to {maxBulkQuantity} assignable from stock.
                                        </Text>
                                    </Stack>
                                ) : (
                                    <Text size="sm" c="dimmed" py="xl" ta="center">
                                        No matching fleet units available.
                                    </Text>
                                )
                            ) : availablePool.length === 0 ? (
                                <Text size="sm" c="dimmed" py="xl" ta="center">
                                    {group.kind === 'worker'
                                        ? 'No workers available.'
                                        : 'No matching fleet units available.'}
                                </Text>
                            ) : (
                                availablePool.map((resource) => (
                                    <ResourceAssignmentCard
                                        key={resource.id}
                                        name={resource.name}
                                        kind={resource.kind}
                                        isPlaceholder={resource.isPlaceholder}
                                        variant="available"
                                        unavailableReason={resource.unavailableReason}
                                        disabled={saving}
                                        rentalAssetPeek={
                                            resource.kind === 'rental_asset'
                                                ? rentalAssetPeekFor(resource.id)
                                                : undefined
                                        }
                                        workerAssignmentPeek={
                                            resource.kind === 'worker'
                                                ? workerAssignmentPeekFor(resource.id)
                                                : undefined
                                        }
                                        onAssign={() => void handleAssign(resource.id)}
                                    />
                                ))
                            )}

                            {group.kind === 'rental_asset' && !loading && (
                                <Stack
                                    gap="sm"
                                    pt="md"
                                    style={{borderTop: '1px solid var(--mantine-color-gray-3)'}}
                                >
                                    <Title order={6}>Third-party rental</Title>
                                    <Text size="sm" c="dimmed">
                                        Assign equipment rented from an outside vendor for this job.
                                    </Text>
                                    {offeringOptions.length === 0 ? (
                                        <Text size="sm" c="dimmed">
                                            No vendor offerings for this asset type.
                                        </Text>
                                    ) : (
                                        <>
                                            <Select
                                                label="Third-party rental"
                                                placeholder="Select a third-party rental"
                                                data={offeringOptions}
                                                value={selectedOfferingId}
                                                onChange={setSelectedOfferingId}
                                                disabled={saving}
                                                searchable
                                                style={{flex: 1}}
                                            />
                                            {isStockType ? (
                                                <NumberInput
                                                    label="Vendor quantity"
                                                    min={1}
                                                    max={requirementGap}
                                                    allowDecimal={false}
                                                    value={vendorQuantity}
                                                    onChange={setVendorQuantity}
                                                    disabled={saving || requirementGap === 0}
                                                />
                                            ) : (
                                                <NumberInput
                                                    label="How many"
                                                    value={offeringQuantity}
                                                    min={1}
                                                    max={500}
                                                    allowDecimal={false}
                                                    disabled={saving}
                                                    onChange={(value) => {
                                                        const next = Number(value)
                                                        setOfferingQuantity(
                                                            Number.isInteger(next) && next > 0
                                                                ? next
                                                                : 1,
                                                        )
                                                    }}
                                                />
                                            )}
                                            <Button
                                                onClick={() => void handleAssignOffering()}
                                                disabled={
                                                    saving ||
                                                    selectedOfferingId == null ||
                                                    (isStockType &&
                                                        (requirementGap < 1 ||
                                                            !Number.isInteger(
                                                                Number(vendorQuantity),
                                                            ) ||
                                                            Number(vendorQuantity) < 1 ||
                                                            Number(vendorQuantity) >
                                                                requirementGap))
                                                }
                                            >
                                                Assign vendor rental
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Grid.Col>
                </Grid>

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Close
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
